/**LAGOSFinancials — /state/lagos/financials */
import React from "react";
import { useLagos } from "@/context/LAGOSContext";
import { PageHeader, SectionCard } from "@/components/ds";

const fmtU=(v)=>{ if(!v)return"—"; const n=Number(v); if(n>=1e9)return`$${(n/1e9).toFixed(1)}B`; if(n>=1e6)return`$${(n/1e6).toFixed(1)}M`; if(n>=1000)return`$${(n/1000).toFixed(0)}K`; return`$${n}`; };
const fmtN=(v,u="")=>{ if(!v&&v!==0)return"—"; return`${Number(v).toLocaleString()} ${u}`.trim(); };

export default function LAGOSFinancials() {
  const { portfolio, loading, cpaPoaData } = useLagos();
  const poa = cpaPoaData?.poa_summary || {};
  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-5">
      <PageHeader label="FINANCIALS" title="" />
      <p className="text-xs text-slate-400 -mt-4">Entity and portfolio-level value · Carbon value (indicative) · Financing readiness</p>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
        <strong>Mandatory DSCR Disclosure:</strong> Carbon and plastic values are indicative. Conditional on VVB validation and registry issuance. Must NOT be included in financing commitments, CRG calculations, or DSCR models until formally validated. Operational circularity revenue (Section A of Carbon Value Bridge) may be included in PFI appraisal subject to independent verification.
      </div>
      <div className="grid grid-cols-2 gap-4">
        <SectionCard title="Section A — Carbon Asset Value (Indicative)">
          <div className="p-4 space-y-2">
            {[["Reduction Potential",fmtN(portfolio.reduction_potential_tco2e,"tCO₂e/yr")],["Carbon Value (Low $10/t)",fmtU(portfolio.carbon_value_low_usd)+"/yr"],["Carbon Value (Base $20/t)",fmtU(portfolio.carbon_value_base_usd)+"/yr"],["Carbon Value (High $40/t)",fmtU(portfolio.carbon_value_high_usd)+"/yr"],["VCM Eligible Entities",portfolio.vcm_eligible_count],["Art.6.4 Eligible Entities",portfolio.art6_eligible_count]].map(([k,v])=>(
              <div key={k} className="flex justify-between text-xs py-1.5 border-b border-slate-50">
                <span className="text-slate-500">{k}</span><span className="font-bold text-emerald-700">{v}</span>
              </div>
            ))}
          </div>
        </SectionCard>
        <SectionCard title="Section B — PoA Carbon Programme">
          <div className="p-4 space-y-2">
            {[["CPAs in PoA",poa.eligible_cpas||0],["PoA Reduction",fmtN(poa.total_reduction||0,"tCO₂e/yr")],["PoA Carbon Value (Base)",fmtU(poa.carbon_value_base_usd||0)+"/yr"],["Coordinating Entity","Lagos State Government (proposed)"],["Status",poa.status||"Design Phase"]].map(([k,v])=>(
              <div key={k} className="flex justify-between text-xs py-1.5 border-b border-slate-50">
                <span className="text-slate-500">{k}</span><span className="font-bold">{v}</span>
              </div>
            ))}
            <p className="text-[10px] text-red-500 pt-1">NOT included in state budget until VVB validation</p>
          </div>
        </SectionCard>
      </div>
      <SectionCard title="Top Entities by Carbon Asset Value">
        <div className="overflow-x-auto p-2">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-slate-100">
              {["Entity","LGA","Reduction (tCO₂e/yr)","Carbon Value (Base)","Pathway","Score"].map(h=><th key={h} className="text-left py-2 px-3 text-slate-400 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {(portfolio.top_opportunities||[]).map((o,i)=>(
                <tr key={o.entity_id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-2 px-3 font-medium max-w-[150px] truncate">{o.name}</td>
                  <td className="py-2 px-3 text-slate-500">{o.lga}</td>
                  <td className="py-2 px-3 text-emerald-700 font-bold">{fmtN(o.reduction_potential_tco2e)}</td>
                  <td className="py-2 px-3">{fmtU(Number(o.reduction_potential_tco2e||0)*20)}/yr</td>
                  <td className="py-2 px-3"><span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-50 text-blue-700 capitalize">{o.carbon_pathway}</span></td>
                  <td className="py-2 px-3 font-black">{(o.composite_score||0).toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
